import { createAsyncThunk, createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchReportsConfigAPI, getReportDataAPI, saveReportConfigAPI } from '../../services/api/reportsConfigService';
import {
  ReportsConfiguration,
  ReportConfigurationMutationPayload,
  ReportColDefinition,
  ReportLocationDetails,
} from '../../types/admin/reportsConfig.types';
import {
  ReportDataState,
  initialReportDataState,
  FetchReportDataParams,
  FetchReportDataResult,
} from '../../types/admin/reportView.types';
import { logger } from 'src/utils/logger';

// ============================================================================
// Type Definitions
// ============================================================================

interface ReportsConfigState {
  data: ReportsConfiguration[];
  isLoading: boolean;
  error: string | null;
  hasAttemptedFetch: boolean;
  hasSuccessfulFetch: boolean;
  lastFetchTimestamp: number | null;
}

interface ReportsConfigSliceState {
  reportsConfig: ReportsConfigState;
  selectedReport: {
    data: ReportsConfiguration | null;
    isLoading: boolean;
    error: string | null;
  };
  /** State for report data (the actual data rows for viewing) */
  reportData: ReportDataState;
}

interface FetchReportsConfigParams {
  forceRefresh?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

// Cache timeout: 5 minutes in milliseconds
const CACHE_TIMEOUT_MS = 5 * 60 * 1000;

// ============================================================================
// Initial State
// ============================================================================

const initialState: ReportsConfigSliceState = {
  reportsConfig: {
    data: [],
    isLoading: false,
    error: null,
    hasAttemptedFetch: false,
    hasSuccessfulFetch: false,
    lastFetchTimestamp: null,
  },
  selectedReport: {
    data: null,
    isLoading: false,
    error: null,
  },
  reportData: initialReportDataState,
};

// ============================================================================
// Async Thunks
// ============================================================================

/**
 * Fetches all reports configuration with optional filtering
 */
export const fetchReportsConfig = createAsyncThunk(
  'reportsConfig/fetchReportsConfig',
  async (params: FetchReportsConfigParams = {}, { rejectWithValue, getState }) => {
    try {
      const state = getState() as { reportsConfig: ReportsConfigSliceState };
      const { hasSuccessfulFetch, lastFetchTimestamp } = state.reportsConfig.reportsConfig;

      // Check if we should skip fetch (unless forced refresh)
      if (!params.forceRefresh && hasSuccessfulFetch && lastFetchTimestamp) {
        const cacheAge = Date.now() - lastFetchTimestamp;
        if (cacheAge < CACHE_TIMEOUT_MS) {
          // Cache is still valid, return existing data
          return state.reportsConfig.reportsConfig.data;
        }
      }

      // Fetch data from API
      const data = await fetchReportsConfigAPI();

      // Parse JSON string fields to typed objects with error handling
      const dataParsed: ReportsConfiguration[] = [];

      for (const report of data) {
        try {
          const colDef: ReportColDefinition[] = report.report_col_definition
            ? JSON.parse(report.report_col_definition)
            : [];
          const locationDetails: ReportLocationDetails | null = report.report_location_details
            ? JSON.parse(report.report_location_details)
            : null;

          dataParsed.push({
            ...report,
            report_col_definition: colDef,
            report_location_details: locationDetails,
          });
        } catch (parseError) {
          const errorMessage =
            parseError instanceof Error
              ? `Failed to parse report configuration for report ID ${report.report_id}: ${parseError.message}`
              : `Failed to parse report configuration for report ID ${report.report_id}`;
          logger.error(errorMessage, parseError instanceof Error ? parseError : new Error(String(parseError)));
          throw new Error(errorMessage);
        }
      }

      return dataParsed;
    } catch (error) {
      const errorMessage = (error as Error).message || 'Failed to fetch reports configuration';
      logger.error('Error in fetchReportsConfig:', error instanceof Error ? error : new Error(String(error)));
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Saves a report configuration (create or update)
 */
export const saveReportConfig = createAsyncThunk(
  'reportsConfig/saveReportConfig',
  async (payload: ReportConfigurationMutationPayload, { rejectWithValue }) => {
    try {
      const data = await saveReportConfigAPI(payload);
      return data;
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to save report configuration');
    }
  }
);

/**
 * Fetches report data (the actual data rows) for a specific report.
 *
 * This thunk implements a metadata-first caching strategy:
 * 1. Service ALWAYS fetches metadata.json from S3 WITHOUT HTTP cache
 * 2. Service compares with localStorage cached metadata (supports multi-report)
 * 3. If metadata changed → Service fetches data WITHOUT cache (hard refresh)
 * 4. If metadata unchanged → Service fetches data WITH browser cache (fast CloudFront)
 *
 * Cache locations:
 * - Metadata: localStorage (persists across page navigations, keyed by report_unique_code)
 * - Data: Browser/CloudFront cache (controlled via Cache-Control headers)
 *
 * This ensures:
 * - Users always see fresh data when the underlying report is updated
 * - Unchanged data is served from browser cache (fast, no S3 round-trip)
 * - Multi-report metadata caching allows fast switching between reports
 *
 * For large datasets (100K+ rows, 50MB+):
 * - Uses streaming where possible
 * - Memory-efficient parsing
 * - Progress indication via loading state
 */
export const fetchReportData = createAsyncThunk<FetchReportDataResult, FetchReportDataParams, { rejectValue: string }>(
  'reportsConfig/fetchReportData',
  async (params: FetchReportDataParams, { rejectWithValue, signal }) => {
    const { reportConfig } = params;
    const reportUniqueCode = reportConfig.report_unique_code;

    try {
      logger.info('fetchReportData: Starting fetch (metadata-first with localStorage + browser cache)', {
        reportUniqueCode,
        locationType: reportConfig.report_location_type,
      });

      // Check if already aborted before starting
      if (signal?.aborted) {
        logger.info('fetchReportData: Aborted before fetch started', { reportUniqueCode });
        return rejectWithValue('Fetch aborted');
      }

      // Pass RTK's built-in signal directly to the service so all S3 calls are abortable
      const response = await getReportDataAPI({
        reportUniqueCode,
        reportLocationType: reportConfig.report_location_type,
        reportLocationDetails: reportConfig.report_location_details,
        signal,
      });

      // Check if aborted after fetch completed
      if (signal?.aborted) {
        logger.info('fetchReportData: Aborted after fetch completed', { reportUniqueCode });
        return rejectWithValue('Fetch aborted');
      }

      const result: FetchReportDataResult = {
        reportUniqueCode,
        data: response.data,
        totalRowCount: response.data.length,
        metadata: response.metadata,
        warnings: response.warnings,
      };

      logger.info('fetchReportData: Fetch completed', {
        reportUniqueCode,
        rowCount: result.totalRowCount,
        metadataTimestamp: response.metadata?.timestamp,
      });

      return result;
    } catch (error) {
      // Don't log abort errors as actual errors
      if (error instanceof Error && error.name === 'AbortError') {
        logger.info('fetchReportData: Fetch was aborted', { reportUniqueCode });
        return rejectWithValue('Fetch aborted');
      }

      const errorMessage = (error as Error).message || 'Failed to fetch report data';
      logger.error('fetchReportData: Error fetching data', {
        reportUniqueCode,
        error: errorMessage,
      });
      return rejectWithValue(errorMessage);
    }
  }
);

/**
 * Toggles the is_active status of a report configuration.
 *
 * This implements a soft-delete pattern:
 * - Setting is_active to false effectively "deletes" the report (hides it from users)
 * - Setting is_active to true "restores" the report (makes it visible again)
 *
 * The record is never permanently deleted from the database, allowing for:
 * - Data recovery if needed
 * - Audit trail preservation
 * - Referential integrity maintenance
 *
 * Use `selectActiveReportsConfig` selector to get only active (non-deleted) reports.
 */
export const toggleReportConfigStatus = createAsyncThunk(
  'reportsConfig/toggleReportConfigStatus',
  async (
    { reportConfig, currentUserAlias }: { reportConfig: ReportsConfiguration; currentUserAlias: string },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const newIsActive = !reportConfig.item_metadata.is_active;
      const currentTimestamp = new Date().toISOString();
      const payload: ReportConfigurationMutationPayload = {
        report_id: reportConfig.report_id,
        report_unique_code: reportConfig.report_unique_code,
        report_name: reportConfig.report_name,
        report_description: reportConfig.report_description,
        report_location_type: reportConfig.report_location_type,
        report_location_details: reportConfig.report_location_details
          ? JSON.stringify(reportConfig.report_location_details)
          : null,
        report_col_definition: JSON.stringify(reportConfig.report_col_definition),
        report_visibility_level: reportConfig.report_visibility_level,
        report_group_name: reportConfig.report_group_name,
        item_metadata: {
          ...reportConfig.item_metadata,
          is_active: newIsActive,
          // Update modification info
          updated_at: currentTimestamp,
          updated_by: currentUserAlias,
        },
      };
      await saveReportConfigAPI(payload);
      // Refresh the full list from the server to reflect the updated state
      await (dispatch as (action: ReturnType<typeof fetchReportsConfig>) => Promise<unknown>)(
        fetchReportsConfig({ forceRefresh: true })
      );
      return { reportId: reportConfig.report_id, isActive: newIsActive };
    } catch (error) {
      return rejectWithValue((error as Error).message || 'Failed to toggle report configuration status');
    }
  }
);

// ============================================================================
// Slice
// ============================================================================

const reportsConfigSlice = createSlice({
  name: 'reportsConfig',
  initialState,
  reducers: {
    /**
     * Clears any error messages
     */
    clearError: (state) => {
      state.reportsConfig.error = null;
      state.selectedReport.error = null;
    },

    /**
     * Clears the selected report error
     */
    clearSelectedReportError: (state) => {
      state.selectedReport.error = null;
    },

    /**
     * Resets the fetch status flags
     */
    resetFetchStatus: (state) => {
      state.reportsConfig.hasAttemptedFetch = false;
      state.reportsConfig.hasSuccessfulFetch = false;
      state.reportsConfig.lastFetchTimestamp = null;
    },

    /**
     * Clears the selected report data
     */
    clearSelectedReport: (state) => {
      state.selectedReport.data = null;
      state.selectedReport.error = null;
    },

    /**
     * Sets the selected report manually
     */
    setSelectedReport: (state, action: PayloadAction<ReportsConfiguration>) => {
      state.selectedReport.data = action.payload;
      state.selectedReport.error = null;
    },

    /**
     * Clears the report data state
     * Should be called when navigating away from report view or when report changes
     */
    clearReportData: (state) => {
      state.reportData = initialReportDataState;
    },

    /**
     * Clears report data error only
     */
    clearReportDataError: (state) => {
      state.reportData.error = null;
    },

    /**
     * Resets all state to initial values
     */
    reset: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // fetchReportsConfig
      .addCase(fetchReportsConfig.pending, (state) => {
        state.reportsConfig.isLoading = true;
        state.reportsConfig.error = null;
      })
      .addCase(fetchReportsConfig.fulfilled, (state, action) => {
        state.reportsConfig.isLoading = false;
        state.reportsConfig.data = action.payload;
        state.reportsConfig.hasAttemptedFetch = true;
        state.reportsConfig.hasSuccessfulFetch = true;
        state.reportsConfig.lastFetchTimestamp = Date.now();
        state.reportsConfig.error = null;
      })
      .addCase(fetchReportsConfig.rejected, (state, action) => {
        state.reportsConfig.isLoading = false;
        state.reportsConfig.error = action.payload as string;
        state.reportsConfig.hasAttemptedFetch = true;
      })
      // saveReportConfig
      .addCase(saveReportConfig.pending, (state) => {
        state.selectedReport.isLoading = true;
        state.selectedReport.error = null;
      })
      .addCase(saveReportConfig.fulfilled, (state) => {
        state.selectedReport.isLoading = false;
        state.selectedReport.error = null;
        // Reset cache to force refresh on next fetch
        state.reportsConfig.lastFetchTimestamp = null;
      })
      .addCase(saveReportConfig.rejected, (state, action) => {
        state.selectedReport.isLoading = false;
        state.selectedReport.error = action.payload as string;
      })
      // toggleReportConfigStatus
      .addCase(toggleReportConfigStatus.pending, (state) => {
        state.selectedReport.isLoading = true;
        state.selectedReport.error = null;
      })
      .addCase(toggleReportConfigStatus.fulfilled, (state) => {
        state.selectedReport.isLoading = false;
        state.selectedReport.error = null;
        // State is refreshed via fetchReportsConfig dispatched inside the thunk
      })
      .addCase(toggleReportConfigStatus.rejected, (state, action) => {
        state.selectedReport.isLoading = false;
        state.selectedReport.error = action.payload as string;
      })
      // fetchReportData
      .addCase(fetchReportData.pending, (state, action) => {
        // Clear previous data when starting new fetch
        state.reportData.data = [];
        state.reportData.isLoading = true;
        state.reportData.error = null;
        state.reportData.reportUniqueCode = action.meta.arg.reportConfig.report_unique_code;
        state.reportData.totalRowCount = 0;
        state.reportData.metadata = null;
      })
      .addCase(fetchReportData.fulfilled, (state, action) => {
        // Only update if the response is for the current report
        if (state.reportData.reportUniqueCode === action.payload.reportUniqueCode) {
          state.reportData.data = action.payload.data;
          state.reportData.totalRowCount = action.payload.totalRowCount;
          state.reportData.metadata = action.payload.metadata ?? null;
          state.reportData.warnings = action.payload.warnings ?? [];
          state.reportData.lastFetchTimestamp = Date.now();
          state.reportData.isLoading = false;
          state.reportData.error = null;
        }
      })
      .addCase(fetchReportData.rejected, (state, action) => {
        // Don't set error state for abort rejections
        if (action.payload !== 'Fetch aborted') {
          state.reportData.error = action.payload as string;
        }
        state.reportData.isLoading = false;
      });
  },
});

// ============================================================================
// Selectors
// ============================================================================

// Define root state type for selectors
type RootState = { reportsConfig: ReportsConfigSliceState };

/**
 * Select all reports configuration
 */
export const selectReportsConfig = (state: RootState): ReportsConfiguration[] => state.reportsConfig.reportsConfig.data;

/**
 * Select only active reports (is_active: true)
 * Memoized to prevent unnecessary re-renders when other state changes
 */
export const selectActiveReportsConfig = createSelector([selectReportsConfig], (reports): ReportsConfiguration[] =>
  reports.filter((report) => report.item_metadata.is_active)
);

/**
 * Select reports config loading state
 */
export const selectReportsConfigIsLoading = (state: RootState): boolean => state.reportsConfig.reportsConfig.isLoading;

/**
 * Select reports config error
 */
export const selectReportsConfigError = (state: RootState): string | null => state.reportsConfig.reportsConfig.error;

/**
 * Select hasAttemptedFetch flag
 */
export const selectReportsConfigHasAttemptedFetch = (state: RootState): boolean =>
  state.reportsConfig.reportsConfig.hasAttemptedFetch;

/**
 * Select a report by ID
 */
export const selectReportById =
  (reportId: number) =>
  (state: RootState): ReportsConfiguration | undefined =>
    state.reportsConfig.reportsConfig.data.find((report) => report.report_id === reportId);

/**
 * Select a report by unique code
 */
export const selectReportByUniqueCode =
  (reportUniqueCode: string) =>
  (state: RootState): ReportsConfiguration | undefined =>
    state.reportsConfig.reportsConfig.data.find((report) => report.report_unique_code === reportUniqueCode);

/**
 * Select report data state
 * Memoized to prevent unnecessary re-renders with large datasets (100K+ rows)
 */
export const selectReportData = createSelector(
  [(state: RootState) => state.reportsConfig.reportData],
  (reportData): Record<string, unknown>[] => reportData.data
);

/**
 * Select report data loading state
 */
export const selectReportDataIsLoading = (state: RootState): boolean => state.reportsConfig.reportData.isLoading;

/**
 * Select report data error
 */
export const selectReportDataError = (state: RootState): string | null => state.reportsConfig.reportData.error;

/**
 * Select report data total row count
 */
export const selectReportDataTotalRowCount = (state: RootState): number => state.reportsConfig.reportData.totalRowCount;

/**
 * Select the report unique code that current data belongs to
 */
export const selectReportDataUniqueCode = (state: RootState): string | null =>
  state.reportsConfig.reportData.reportUniqueCode;

/**
 * Select the report S3 metadata (timestamp, last_updated_by)
 * Only present for folder-based S3 reports
 */
export const selectReportDataMetadata = (state: RootState) => state.reportsConfig.reportData.metadata;

/**
 * Select warnings about partial failures when loading report data
 * Present when some files failed to load but graceful degradation was enabled
 */
export const selectReportDataWarnings = (state: RootState) => state.reportsConfig.reportData.warnings;

// ============================================================================
// Exports
// ============================================================================

// Export actions
export const {
  clearError,
  clearSelectedReportError,
  resetFetchStatus,
  clearSelectedReport,
  setSelectedReport,
  clearReportData,
  clearReportDataError,
  reset,
} = reportsConfigSlice.actions;

// Export reducer
export default reportsConfigSlice.reducer;
